import React from 'react';
import { useIntl } from 'react-intl';

import { createKeyboardClickHandler } from '@/utils/accessibility';

import { CropImage } from '@/components/CropImage/CropImage';
import { Modal } from '@/components/Modal';
import { useRouter } from '@/hooks/useRouter';
import {
  DEFAULT_STRAP_PREVIEW_COORDS,
  DEFAULT_STRAP_REFERENCE_DIMENSIONS,
  inferSourceDimensionsFromUrl,
} from '@/lib/cdn/cropTransform';
import { pdpMessages } from '@/messages/product';

import type { BundleVariationOption } from '../ProductDetailSection';

import * as S from './ChangeStrapModal.styles';

export interface ChangeStrapModalProps {
  bundleVariationOptions: BundleVariationOption[];
  close: () => void;
  isOpen: boolean;
  onClose: () => void;
  onOptionClick?: (option: BundleVariationOption) => void;
  scrollToTopWhenOpen?: boolean;
}

const METAL_BRACELET = 'V-STRAP-COLOR-6666';

const toCropImageDimensions = (dimensions: {
  height: number;
  width: number;
}) => ({
  x: dimensions.width,
  y: dimensions.height,
});

const getModalCropImageDimensions = (optionImageSrc: string) => {
  const thumbnailSizeMatch = optionImageSrc.match(/_thumbnail_(\d+)\./i);
  const thumbnailSize = thumbnailSizeMatch?.[1]
    ? Number(thumbnailSizeMatch[1])
    : undefined;
  const referenceDimensions =
    thumbnailSize && Number.isFinite(thumbnailSize)
      ? { height: thumbnailSize, width: thumbnailSize }
      : DEFAULT_STRAP_REFERENCE_DIMENSIONS;

  const inferredSourceDimensions = inferSourceDimensionsFromUrl(
    optionImageSrc,
    referenceDimensions,
  );

  return {
    originalImageDimensions: toCropImageDimensions(referenceDimensions),
    sourceImageDimensions: inferredSourceDimensions
      ? toCropImageDimensions(inferredSourceDimensions)
      : undefined,
  };
};

const ChangeStrapModal: React.FC<ChangeStrapModalProps> = ({
  bundleVariationOptions,
  close,
  isOpen,
  onClose,
  onOptionClick,
  scrollToTopWhenOpen,
}) => {
  const router = useRouter();
  const { formatMessage } = useIntl();
  const handleOptionClick = (option: BundleVariationOption) => {
    if (onOptionClick) {
      onOptionClick(option);
    } else if (option.slug) {
      const pathWithoutQuery = router.asPath.split('?')[0];
      const path = pathWithoutQuery.endsWith('/')
        ? pathWithoutQuery.slice(0, -1)
        : pathWithoutQuery;
      const newPath = path
        .split('/')
        .slice(0, -1)
        .concat(option.slug)
        .join('/');
      router.push(newPath);
      close();
    }
  };

  return (
    <Modal
      alignItems='center'
      data-test-id='ChangeStrapModal'
      gutters={false}
      height='auto'
      isOpen={isOpen}
      onClose={onClose}
      scrollToTopWhenOpen={scrollToTopWhenOpen}
    >
      <Modal.Header data-test-id='ChangeStrapModalHeader' onClose={onClose}>
        <h2>{formatMessage(pdpMessages.chooseStrap)}</h2>
      </Modal.Header>
      <Modal.Body data-test-id='ChangeStrapModalBody'>
        <S.OptionsContainer data-test-id='ChangeStrapModalOptionsContainer'>
          {bundleVariationOptions.map((option, index) => {
            const Div =
              option === bundleVariationOptions[0]
                ? S.SelectedOptionBox
                : S.OptionBox;
            const resolvedImageSrc = option?.image ?? '';
            const { originalImageDimensions, sourceImageDimensions } =
              getModalCropImageDimensions(resolvedImageSrc);
            return (
              <Div
                key={index}
                data-test-id={`ChangeStrapModalOption-${index}`}
                onClick={() => handleOptionClick(option)}
                onKeyDown={createKeyboardClickHandler(() =>
                  handleOptionClick(option),
                )}
                role='button'
                tabIndex={0}
              >
                <CropImage
                  alt={option.alt ?? ''}
                  coords={option.coords ?? DEFAULT_STRAP_PREVIEW_COORDS}
                  data-test-id={`ChangeStrapModalOption${index}-Image`}
                  height={41}
                  loading='lazy'
                  mobileWidth={180}
                  originalImageDimensions={originalImageDimensions}
                  sizes='50vw'
                  sourceImageDimensions={sourceImageDimensions}
                  src={resolvedImageSrc}
                  strapCropPreset='modal'
                  useLoader={false}
                  width={180}
                />
                <S.StrapInfo>
                  <S.StrapColor
                    data-test-id={`ChangeStrapModalOption${index}-StrapColor`}
                  >
                    {option.strapColorCode === METAL_BRACELET
                      ? option.strapColor
                      : option.strapMaterial}
                  </S.StrapColor>
                  <S.Slug data-test-id={`ChangeStrapModalOption${index}-Slug`}>
                    {option.slug}
                  </S.Slug>
                  <S.StrapMaterial
                    data-test-id={`ChangeStrapModalOption${index}-StrapMaterial`}
                  >
                    {option.strapColorCode === METAL_BRACELET
                      ? option.strapMaterial
                      : option.strapColor}
                  </S.StrapMaterial>
                </S.StrapInfo>
              </Div>
            );
          })}
        </S.OptionsContainer>
      </Modal.Body>
    </Modal>
  );
};

export default ChangeStrapModal;
